import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { AlertOctagon, CheckCircle2, Copy, XCircle } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { publicApi, smsApi } from "../../../api/resources";
import type { CompliancePanel, ConsentText, TemplateInfo } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { ThemeSwitcher } from "../../../components/ThemeSwitcher";
import { Button, TextInput } from "../../../components/ui";
import { LoadState, useLoad } from "../shared";
import { TemplateCard } from "./TemplateCard";
import { TimeclockSettings } from "./TimeclockSettings";
import { TextFeaturesCard } from "./TextFeaturesCard";

const LINKS: [string, string, string][] = [
  ["sms_help_url", "SMS Help URL", "Linked from the HELP reply. While blank, {help_url} renders as the admin-contact sentence."],
  ["privacy_policy_url", "Privacy Policy URL", "Shown with the SMS consent statement."],
  ["terms_url", "Terms URL", "Shown with the SMS consent statement."],
  ["sms_support_line", "Support line", "Optional; rendered by {support_line}. 40 characters max."],
];

function H2({ children }: { children: string }) {
  return <h2 className="mb-4 font-display text-2xl font-bold uppercase tracking-tight text-white">{children}</h2>;
}

function Compliance({ c }: { c: CompliancePanel }) {
  const copy = (v: string) => navigator.clipboard?.writeText(v).then(() => toast.success("Copied"));
  return (
    <section data-testid="sms-compliance-panel" className="rounded-sm border border-white/10 bg-surface p-6">
      <H2>SMS compliance</H2>
      {c.blockers.length > 0 && (
        <div data-testid="sms-blockers-banner" className="mb-6 rounded-sm border border-signal/50 bg-signal/10 p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><AlertOctagon className="h-4 w-4 text-signal" /> Production blockers</p>
          <ul className="list-disc space-y-1 pl-6 text-sm text-red-100">{c.blockers.map((b) => <li key={b} data-testid="sms-blocker-item">{b}</li>)}</ul>
        </div>
      )}
      <dl className="grid gap-5 md:grid-cols-3">
        <div><dt className="label-caps text-zinc-500">Provider mode</dt><dd data-testid="compliance-provider-mode" className="mt-1 font-mono text-lg text-white">{c.provider_mode}</dd><p className="text-xs text-zinc-500">SMS_PROVIDER={c.configured_provider} · {c.app_env}</p></div>
        <div><dt className="label-caps text-zinc-500">A2P 10DLC approved</dt><dd data-testid="compliance-a2p" className={`mt-1 text-lg ${c.a2p_10dlc_approved ? "text-emerald-300" : "text-signal"}`}>{c.a2p_10dlc_approved ? "Yes" : "No"}</dd></div>
        <div><dt className="label-caps text-zinc-500">Opt-out handling</dt><dd data-testid="compliance-opt-out-mode" className="mt-1 text-sm text-white">{c.opt_out_mode === "TWILIO_ADVANCED_OPT_OUT" ? "Twilio replies to STOP/START" : "App sends STOP/START replies"}</dd></div>
      </dl>
      <h3 className="label-caps mb-2 mt-6 text-zinc-400">Twilio configuration (values never shown)</h3>
      <ul className="grid gap-1 sm:grid-cols-2">
        {c.twilio_config.map((t) => (
          <li key={t.name} data-testid={`compliance-config-${t.name.split(" ")[0].toLowerCase()}`} className="flex items-center gap-2 text-sm text-zinc-300">
            {t.present ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-zinc-500" />}{t.name}
          </li>
        ))}
      </ul>
      <h3 className="label-caps mb-2 mt-6 text-zinc-400">Webhook URLs to paste into Twilio</h3>
      {[["Incoming messages", c.inbound_webhook_url, "inbound"], ["Status callback", c.status_callback_url, "status"]].map(([l, v, k]) => (
        <div key={k} className="mb-2 flex items-center gap-2">
          <span className="w-36 text-xs text-zinc-500">{l}</span>
          <code data-testid={`compliance-webhook-${k}`} className="flex-1 truncate rounded-sm bg-ink px-2 py-1.5 font-mono text-xs text-zinc-200">{v}</code>
          <button type="button" onClick={() => copy(v)} aria-label={`Copy ${l}`} data-testid={`compliance-copy-${k}`} className="p-1 text-zinc-400 hover:text-white"><Copy className="h-4 w-4" /></button>
        </div>
      ))}
    </section>
  );
}

function LinkSetting({ k, label, help, initial, onSaved }: { k: string; label: string; help: string; initial: string; onSaved: () => void }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await smsApi.setTextSetting(k, value);
      toast.success(`${label} saved`);
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`setting-${k}`} className="grid gap-2 border-b border-white/5 py-4 md:grid-cols-[200px_1fr_auto] md:items-center">
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        {!initial && k !== "sms_support_line" && <p data-testid={`setting-${k}-blank`} className="text-[11px] font-semibold uppercase tracking-wider text-signal">Not set · blocker</p>}
      </div>
      <div>
        <TextInput value={value} onChange={(e) => setValue(e.target.value)} placeholder={k === "sms_support_line" ? "Optional" : "https://"} data-testid={`setting-${k}-input`} />
        <p className="mt-1 text-xs text-zinc-500">{help}</p>
      </div>
      <Button variant="secondary" className="h-9 px-4 text-[11px]" disabled={value === initial} loading={busy} onClick={save} data-testid={`setting-${k}-save`}>Save</Button>
    </div>
  );
}

export default function AdminSettings() {
  const [reload, setReload] = useState(0);
  const bump = () => setReload((r) => r + 1);
  const cLoader = useCallback(() => smsApi.compliance(), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const sLoader = useCallback(() => smsApi.settings(), [reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: compliance, error } = useLoad<CompliancePanel>(cLoader);
  const { data: settings } = useLoad(sLoader);
  const [templates, setTemplates] = useState<TemplateInfo[] | null>(null);
  const [consent, setConsent] = useState<ConsentText | null>(null);
  useEffect(() => {
    smsApi.templates().then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
    publicApi.consentText().then(setConsent).catch(() => setConsent(null));
  }, [reload]);
  const val = (k: string) => String(settings?.find((s) => s.key === k)?.value ?? "");

  return (
    <section data-testid="admin-settings-page" className="animate-rise space-y-10">
      <PageHeader title="Settings">
        <Link to="/admin/settings/twilio" className="mr-4 text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid="settings-twilio-link">Twilio connection →</Link>
        <Link to="/admin/settings/email" className="mr-4 text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid="settings-email-link">Email →</Link>
        <Link to="/admin/settings/launch-readiness" className="mr-4 text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid="settings-launch-readiness-link">Launch readiness →</Link>
        <Link to="/admin/settings/security" className="mr-4 text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid="settings-security-link">My security →</Link>
        <Link to="/admin/settings/branding" className="mr-4 text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid="settings-branding-link">Branding files →</Link>
        <Link to="/admin/settings/administrators" className="text-xs font-semibold uppercase tracking-[0.12em] text-signal hover:text-white" data-testid="settings-administrators-link">Administrators →</Link>
      </PageHeader>
      <div className="mb-6"><ThemeSwitcher /></div>
      <LoadState error={error} loading={!compliance && !error} testId="settings-load-error" />
      {compliance && <Compliance c={compliance} />}
      <TimeclockSettings />
      <TextFeaturesCard />
      {settings && (
        <section data-testid="sms-link-settings" className="rounded-sm border border-white/10 bg-surface p-6">
          <H2>Links & help</H2>
          {LINKS.map(([k, l, h]) => <LinkSetting key={`${k}-${val(k)}`} k={k} label={l} help={h} initial={val(k)} onSaved={bump} />)}
        </section>
      )}
      {consent && (
        <section data-testid="consent-draft-card" className="rounded-sm border border-warn/40 bg-warn/5 p-6">
          <H2>SMS consent statement</H2>
          <p data-testid="consent-draft-label" className="label-caps mb-3 text-warn">{consent.label} · version {consent.version}</p>
          <p data-testid="consent-draft-text" className="text-sm leading-relaxed text-zinc-200">{consent.text}</p>
          <p className="mt-3 text-xs text-zinc-400">Privacy Policy: {consent.privacy_policy_url ?? "not set"} · Terms: {consent.terms_url ?? "not set"}. Shown on the registration page; the version is stored on every consent record.</p>
        </section>
      )}
      <section data-testid="templates-section">
        <H2>Text message templates</H2>
        <p className="mb-5 max-w-3xl text-sm text-zinc-400">Allowed placeholders: <code className="font-mono text-zinc-200">{"{first_name} {next_shift} {help_url} {support_line} {brand} {punch_type} {punch_dir} {venue} {link} {expires} {reason} {correction_link} {local_time} {tz} {sent_time}"}</code>. Every template must include “GamedayTime”; HELP must include STOP wording.</p>
        {!templates && <LoadState error={null} loading testId="templates-loading" />}
        <div className="grid gap-4 xl:grid-cols-2">
          {templates?.map((t) => <TemplateCard key={t.key} t={t} onSaved={(nt) => setTemplates((ts) => ts?.map((x) => (x.key === nt.key ? nt : x)) ?? null)} />)}
        </div>
      </section>
    </section>
  );
}
